import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { BiImage } from 'react-icons/bi';

import { AxiosError } from 'axios';

import { FormHandles } from '@unform/core';

import Input from '../../../../../../newComponents/UnformInput';
import UnformFiscalCodeAutocomplete from '../../../../../../newComponents/UnformFiscalCodeAutocomplete';
import FileInput from '../../../../../../newComponents/FileInput';
import Select from '../../../../../../newComponents/UnformSelect';
import TextArea from '../../../../../../newComponents/UnformTextArea';

import {
  Option,
  useSimpleProduct,
} from '../../../../../../hooks/simpleProduct';
import { useAuth } from '../../../../../../hooks/auth';
import { useToast } from '../../../../../../hooks/toast';

import { Container, HelpButton } from './styles';

import productService from '../../../../../../services/ProductsService';
import {
  buscarNcm,
  buscarCest,
} from '../../../../../../services/fiscalCatalogService';

import { newDebounce } from '../../../../../../utils/debounce';

import { CustomInput } from '../../../../styles';
import Toggle from '../../../../../../newComponents/Toggle';

type DetailsProps = {
  formRef: React.RefObject<FormHandles>;
  categoriasFiscaisOptions: Option[];
  emissaoHabilitada: boolean;
};

// Origem da mercadoria (NFC-e) — códigos 0 a 8 (espelha a tela legada).
export const ORIGEM_OPTIONS: Option[] = [
  { value: 0, label: '0 - Nacional' },
  { value: 1, label: '1 - Estrangeira (importação direta)' },
  { value: 2, label: '2 - Estrangeira (adquirida no mercado interno)' },
  { value: 3, label: '3 - Nacional (>40% de conteúdo importado)' },
  { value: 4, label: '4 - Nacional (processos produtivos básicos)' },
  { value: 5, label: '5 - Nacional (<40% de conteúdo importado)' },
  { value: 6, label: '6 - Estrangeira (importação direta, sem similar)' },
  { value: 7, label: '7 - Estrangeira (mercado interno, sem similar)' },
  { value: 8, label: '8 - Nacional (conteúdo importado >70%)' },
];

export const DetailsFormSchema = Yup.object().shape({
  group: Yup.number()
    .integer('Grupo inválido')
    .positive('Grupo inválido')
    .required('O grupo é obrigatório'),
  name: Yup.string()
    .max(60, 'No máximo 60 caracteres')
    .required('O nome é obrigatório'),
  observation: Yup.string().max(500, 'No máximo 500 caracteres'),
  code: Yup.string(),
  gtin: Yup.string(),
  unitOfMeasurement: Yup.number()
    .integer('Unidade de medida inválida')
    .positive('Unidade de medida inválida')
    .required('A unidade de medida é obrigatório'),
  minimumStock: Yup.number()
    .nullable(true)
    .transform((_, value) => (value ? Number(value) : null)),
  currentStock: Yup.number()
    .nullable(true)
    .transform((_, value) => (value ? Number(value) : null)),
  addStock: Yup.number().transform((_, value) => (value ? Number(value) : 0)),
  portion: Yup.number()
    .min(0.001, 'Fração inválida')
    .nullable(true)
    .transform((_, value) => (value ? Number(value) : null)),
});

const Details: React.FC<DetailsProps> = (props) => {
  const { formRef, categoriasFiscaisOptions, emissaoHabilitada } = props;
  const { user } = useAuth();
  const { addToast } = useToast();
  const {
    id,
    image,
    groupsOptions,
    isDrawerOpened,
    productPortion,
    ncmOverride,
    cfopOverride,
    cestOverride,
    origemOverride,
    unitsOfMeasurementOptions,
    handleChangeProduct,
    handleChangeProductPortion,
  } = useSimpleProduct();

  const [canChangeCurrentStock, setCanChangeCurrentStock] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [codeIsValid, setCodeIsValid] = useState(false);
  const [unitOfMeasurement, setUnitOfMeasurement] = useState('UN');
  const [showOverrides, setShowOverrides] = useState(false);
  // Espelha o NCM digitado no override só para filtrar a lista de CEST — o
  // valor que vai no submit é o do unform, não este.
  const [ncmOverrideAtual, setNcmOverrideAtual] = useState(ncmOverride || '');

  // Prefill: ao abrir o drawer de um produto que já tem override, o valor chega
  // pelo hook (e pelo setData do unform), sem passar pelo onValueChange.
  useEffect(() => {
    setNcmOverrideAtual(ncmOverride || '');
  }, [ncmOverride]);

  const handleChangeImage = useCallback(
    (imageToChange?: File) => {
      handleChangeProduct({ image: imageToChange });
    },
    [handleChangeProduct],
  );

  const handleToggleCanChangeCurrentStock = useCallback(() => {
    setCanChangeCurrentStock((oldState) => !oldState);
  }, []);

  const req = async (code: string): Promise<void> => {
    formRef.current?.setErrors({});
    setCodeIsValid(false);
    setIsLoading(true);

    if (code) {
      try {
        const data2 = {
          code,
        };

        if (typeof id === 'number') {
          Object.assign(data2, {
            productId: id,
          });
        }

        await productService.validatePDVCode(user.empresa_id, data2);

        setCodeIsValid(true);
        setIsLoading(false);
      } catch (err) {
        console.log(err);

        formRef.current?.setErrors({
          code: 'Código já em uso',
        });
        addToast({
          title: (err as AxiosError).message,
          type: 'error',
        });
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const reqDebouce: (code: string) => void = newDebounce(
    async (code: string): Promise<void> => req(code),
    2000,
  );

  useEffect(() => {
    if (!isDrawerOpened) {
      setCanChangeCurrentStock(false);
      setCodeIsValid(false);
      setShowOverrides(false);
      return;
    }
    // Auto-expande a seção de overrides quando o produto já tem algum, para o
    // usuário VER os valores (os campos ficam sempre montados para o prefill/save
    // funcionar, mas ficam escondidos quando colapsados).
    const hasOverride =
      !!ncmOverride ||
      !!cfopOverride ||
      !!cestOverride ||
      (origemOverride !== undefined &&
        origemOverride !== null &&
        String(origemOverride) !== '');
    if (hasOverride) {
      setShowOverrides(true);
    }
  }, [isDrawerOpened, ncmOverride, cfopOverride, cestOverride, origemOverride]);

  return (
    <Container isEditing={canChangeCurrentStock}>
      <FileInput
        id="firstSimpleProductImage"
        icon={BiImage}
        label="Imagem principal"
        title="Escolha uma imagem"
        selectedImage={image}
        acceptableTypes=".jpeg,.jpg,.png"
        subtitle="Formatos: .jpeg, .jpg, .png"
        onRemove={() => handleChangeImage(undefined)}
        onSelectFile={handleChangeImage}
      />
      <Select
        id="simpleProductGroup"
        name="group"
        label="Grupo"
        placeholder="Selecione uma opção"
        maxMenuHeight={140}
        options={groupsOptions}
      />
      <Input
        id="simpleProductName"
        name="name"
        label="Nome do produto"
        placeholder="Ex.: Lasanha"
      />
      <TextArea
        id="simpleProductObservation"
        name="observation"
        label="Descrição"
        placeholder="Ex.: Aproximadamente 700g"
      />
      <CustomInput
        isLoading={isLoading}
        codeIsValid={codeIsValid}
        id="code"
        name="code"
        placeholder="Ex.: 632"
        label="Código PDV"
        onChange={(e) => reqDebouce(e.target.value)}
      />
      <Select
        id="simpleProductUnitOfMeasurement"
        name="unitOfMeasurement"
        label="Unidade de medida"
        placeholder="Selecione uma opção"
        maxMenuHeight={140}
        onChange={(option) =>
          setUnitOfMeasurement(
            (option as Option).label.split('(')[1].split(')')[0],
          )
        }
        options={unitsOfMeasurementOptions}
      />
      <div>
        <Input
          id="simpleProductCurrentStock"
          type="number"
          name="currentStock"
          placeholder="Ex.: 80"
          label="Estoque atual"
          disabled={typeof id === 'number'}
        />
        {typeof id === 'number' && (
          <HelpButton type="button" onClick={handleToggleCanChangeCurrentStock}>
            Alterar
          </HelpButton>
        )}
      </div>
      <Input
        id="simpleProductMinimumStock"
        type="number"
        name="minimumStock"
        placeholder="Ex.: 5"
        label="Estoque mínimo"
      />
      <div>
        <label
          htmlFor="fractionedProduct"
          style={{
            display: 'block',
            marginBottom: 6,
            fontWeight: 500,
            fontSize: 14,
            fontFamily: "'Sora', sans-serif",
            color: '#101d40',
          }}
        >
          Produto fracionado
        </label>
        <Toggle
          firstText="Não"
          secondText="Sim"
          checked={productPortion}
          onToggle={handleChangeProductPortion}
        />
      </div>
      <div>
        <label
          htmlFor="portion"
          style={{
            display: 'block',
            marginBottom: 6,
            fontWeight: 500,
            fontSize: 14,
            fontFamily: "'Sora', sans-serif",
            color: '#101d40',
          }}
        >
          Fração
        </label>
        <div>
          <Input
            id="portion"
            disabled={!productPortion}
            name="portion"
            placeholder="Ex.: 1000"
            type="number"
            step=".001"
            min={0.001}
          />
          <span className={!productPortion ? 'disabled' : ''}>
            {unitOfMeasurement}
          </span>
        </div>
      </div>
      <Input
        id="simpleProductGtin"
        name="gtin"
        label="Código de barras"
        placeholder="Ex.: 723648234"
      />
      <Select
        id="simpleProductCategoriaFiscal"
        name="categoriaFiscal"
        label={`Categoria Fiscal${emissaoHabilitada ? ' (obrigatória)' : ''}`}
        placeholder="Selecione a categoria fiscal"
        maxMenuHeight={140}
        isClearable
        options={categoriasFiscaisOptions}
      />
      <button
        type="button"
        onClick={() => setShowOverrides((prev) => !prev)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: '#e02020',
          fontWeight: 500,
          fontSize: 14,
          fontFamily: "'Sora', sans-serif",
          textAlign: 'left',
        }}
      >
        {showOverrides
          ? 'Ocultar exceções fiscais'
          : 'Exceções fiscais deste produto (opcional)'}
      </button>
      {/* Sempre montados (para prefill/save do unform funcionarem); apenas
          escondidos via CSS quando a seção está colapsada. */}
      <div style={{ display: showOverrides ? 'contents' : 'none' }}>
        <UnformFiscalCodeAutocomplete
          id="simpleProductNcmOverride"
          name="ncmOverride"
          label="NCM (exceção)"
          placeholder="Código ou descrição. Ex.: 21069090"
          loadOptions={buscarNcm}
          onValueChange={setNcmOverrideAtual}
        />
        <Input
          id="simpleProductCfopOverride"
          name="cfopOverride"
          label="CFOP (exceção)"
          placeholder="Ex.: 5102"
        />
        <UnformFiscalCodeAutocomplete
          id="simpleProductCestOverride"
          name="cestOverride"
          label="CEST (exceção)"
          placeholder="7 dígitos ou descrição"
          // Filtrado pelo NCM do override; sem NCM aqui, cai na busca geral.
          loadOptions={(termo) =>
            buscarCest({ ncm: ncmOverrideAtual, search: termo })
          }
          reloadKey={ncmOverrideAtual}
          loadOnOpen
        />
        <Select
          id="simpleProductOrigemOverride"
          name="origemOverride"
          label="Origem (exceção)"
          placeholder="Origem da mercadoria"
          maxMenuHeight={140}
          isClearable
          options={ORIGEM_OPTIONS}
        />
      </div>
      {canChangeCurrentStock && (
        <Input
          id="simpleProductAddStock"
          type="number"
          name="addStock"
          placeholder="Ex.: 80"
          label="Alterar estoque"
          disabled={typeof id !== 'number'}
          autoFocus
        />
      )}
    </Container>
  );
};

export default Details;
